import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type { ReleaseDetail } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";

/**
 * Freezing the scope (§13).
 *
 * <p>Says what will happen before it happens, because this is the moment the release makes a
 * promise: a snapshot is taken, and from here every addition and removal costs a coded reason.
 * Neither is obvious from a button labelled "Freeze scope", and both are things people want to
 * know before rather than after.
 */
export function FreezeScopeDrawer({
  release,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (open) {
      setReason("");
      reset();
    }
    // reset is stable for the life of the drawer; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = () =>
    void run(
      () =>
        api.post(`/api/v1/releases/${release.id}/freeze-scope`, {
          reason: reason.trim() === "" ? null : reason.trim(),
          lockVersion: release.lockVersion,
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Freeze the scope"
      subtitle={`${release.releaseKey} — ${release.releaseName}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving}>
            {saving ? "Freezing…" : "Freeze scope"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <div className="rounded-lg border border-ink-200 bg-ink-50 px-3 py-2.5 text-sm">
          <p className="font-medium text-ink-900">What this does</p>
          <ul className="mt-1.5 list-inside list-disc space-y-1 text-ink-700">
            <li>
              Takes a snapshot of the {release.scope.total} items in this release, exactly as they
              are now. It is never edited afterwards.
            </li>
            <li>
              From here, adding or removing work needs a coded reason and lands in the history.
            </li>
            <li>Nothing is locked. Scope can still change — it just has to say why.</li>
          </ul>
        </div>

        <Field label="Why now" hint="Optional. Kept with the snapshot.">
          <Textarea
            rows={3}
            value={reason}
            placeholder="Development complete, handing to QA."
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}
